import React, { useEffect, useState, useCallback, useRef, useImperativeHandle, forwardRef } from "react";
import { FaPlay, FaPause, FaBackward, FaForward } from "react-icons/fa";

// Extracted from src/pages/EpisodeTimestamps.js (2026-07-06) so the
// Episode Metadata Editor can use the exact same compact player instead of
// a similar-but-separate one. Imperative API (seekTo/seekBy/playFrom) lets
// a parent page trigger seeks from its own "▶ play this segment" buttons
// without lifting all player state up into the parent.

function formatSeconds(sec) {
  if (sec == null || sec === "" || isNaN(sec)) return "";
  sec = Math.floor(Number(sec));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

const CompactYouTubePlayer = forwardRef(function CompactYouTubePlayer(
  { videoId, onTimeUpdate, sticky = true, elementId },
  ref
) {
  const domId = elementId || "yt-player";
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const playerRef = useRef(null);
  const timeIntervalRef = useRef(null);

  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(tag);
    }
  }, []);

  const createPlayer = useCallback((vid) => {
    if (playerRef.current) {
      try { playerRef.current.destroy(); } catch (e) {}
      playerRef.current = null;
    }
    if (timeIntervalRef.current) clearInterval(timeIntervalRef.current);

    const initPlayer = () => {
      playerRef.current = new window.YT.Player(domId, {
        videoId: vid,
        playerVars: { autoplay: 0, rel: 0, modestbranding: 1 },
        events: {
          onReady: () => {
            timeIntervalRef.current = setInterval(() => {
              if (playerRef.current?.getCurrentTime) {
                const t = Math.floor(playerRef.current.getCurrentTime());
                setCurrentTime(t);
                if (onTimeUpdate) onTimeUpdate(t);
              }
              if (playerRef.current?.getPlayerState) {
                setIsPlaying(playerRef.current.getPlayerState() === 1);
              }
            }, 250);
          },
        },
      });
    };

    if (window.YT && window.YT.Player) {
      initPlayer();
    } else {
      window.onYouTubeIframeAPIReady = initPlayer;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domId]);

  useEffect(() => {
    if (videoId) createPlayer(videoId);
    return () => {
      if (timeIntervalRef.current) clearInterval(timeIntervalRef.current);
      if (playerRef.current) {
        try { playerRef.current.destroy(); } catch (e) {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  const seekTo = useCallback((sec) => {
    if (playerRef.current?.seekTo) {
      playerRef.current.seekTo(Math.max(0, sec), true);
    }
  }, []);

  const seekBy = useCallback((delta) => {
    if (playerRef.current?.getCurrentTime && playerRef.current?.seekTo) {
      const cur = playerRef.current.getCurrentTime();
      playerRef.current.seekTo(Math.max(0, cur + delta), true);
    }
  }, []);

  const togglePlay = useCallback(() => {
    if (!playerRef.current) return;
    if (isPlaying) {
      playerRef.current.pauseVideo();
    } else {
      playerRef.current.playVideo();
    }
  }, [isPlaying]);

  const playFrom = useCallback((sec) => {
    if (sec != null && !isNaN(sec)) {
      seekTo(sec);
      if (playerRef.current?.playVideo) playerRef.current.playVideo();
    }
  }, [seekTo]);

  useImperativeHandle(ref, () => ({
    seekTo, seekBy, togglePlay, playFrom,
    getCurrentTime: () => playerRef.current?.getCurrentTime?.() ?? 0,
  }), [seekTo, seekBy, togglePlay, playFrom]);

  return (
    <div className={sticky ? "sticky top-0 z-30 bg-kk-ink border-b border-kk-gold/10" : ""}>
      <div className="max-w-4xl mx-auto">
        <div className="relative w-full" style={{ paddingBottom: "36%" }}>
          <div id={domId} className="absolute inset-0 w-full h-full" />
        </div>

        <div className="px-4 py-2 flex items-center justify-center gap-1 flex-wrap">
          {[
            { label: "-1m", sec: -60 },
            { label: "-15s", sec: -15 },
            { label: "-5s", sec: -5 },
          ].map((b) => (
            <button
              key={b.label}
              onClick={() => seekBy(b.sec)}
              className="px-2 py-1 bg-white/5 border border-kk-gold/15 rounded text-kk-gold/70 text-[11px] font-mono
                hover:bg-kk-gold/10 hover:border-kk-gold/30 hover:text-kk-gold active:scale-95 transition-all"
            >
              <FaBackward className="inline mr-0.5 text-[7px] opacity-50" />{b.label}
            </button>
          ))}

          <button
            onClick={togglePlay}
            className="px-3 py-1 bg-kk-gold/10 border border-kk-gold/30 rounded text-kk-gold text-xs
              hover:bg-kk-gold/20 active:scale-95 transition-all"
          >
            {isPlaying ? <FaPause className="text-[10px]" /> : <FaPlay className="text-[10px]" />}
          </button>

          <div className="px-3 py-1 bg-kk-maroon/30 border border-kk-gold/20 rounded text-kk-cream font-mono text-sm
            min-w-[90px] text-center tabular-nums select-all cursor-pointer"
            title="Click to copy current time"
          >
            {formatSeconds(currentTime)}
          </div>

          {[
            { label: "+5s", sec: 5 },
            { label: "+15s", sec: 15 },
            { label: "+1m", sec: 60 },
          ].map((b) => (
            <button
              key={b.label}
              onClick={() => seekBy(b.sec)}
              className="px-2 py-1 bg-white/5 border border-kk-gold/15 rounded text-kk-gold/70 text-[11px] font-mono
                hover:bg-kk-gold/10 hover:border-kk-gold/30 hover:text-kk-gold active:scale-95 transition-all"
            >
              {b.label}<FaForward className="inline ml-0.5 text-[7px] opacity-50" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
});

export default CompactYouTubePlayer;
export { formatSeconds };
